import { css } from '@emotion/react';
import { blueGrey, grey } from '@mui/material/colors';

import { campaignManagerTheme } from '@campaign-manager/theme';

export const tableContainerWrapper = css`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

export const tableContainer = css`
  overflow: auto;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  /*
   * Force an always-visible, styled scrollbar. Without this, macOS renders
   * overlay scrollbars that auto-hide even while the table overflows, so
   * mouse-only users (notably Windows enterprise) have no discoverable way to
   * scroll horizontally — the wheel only scrolls vertically. Styling the bar
   * flips it to a classic, persistent scrollbar on every platform.
   */

  /* Firefox + standard (Baseline 2024): forces a visible, styled scrollbar */
  scrollbar-width: auto;
  scrollbar-color: ${blueGrey[200]} transparent;

  /* Chromium (Chrome/Edge): any ::-webkit-scrollbar rule forces classic bars */
  &::-webkit-scrollbar {
    width: 12px;
    height: 12px; /* height controls the horizontal bar thickness */
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${blueGrey[200]};
    border-radius: 6px;
    border: 3px solid transparent; /* inset padding so the thumb reads thinner */
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${blueGrey[400]};
  }
`;

export const table = css`
  width: 100%;
  table-layout: auto;
  border-spacing: 0px;
  border-collapse: collapse;
  border-bottom: 1px solid ${blueGrey[50]};
  position: relative;
`;

export const tableEdgeState = css`
  flex: 1 1 auto;
`;

export const thead = css`
  background: #ffffff;
`;

export const thStickyHead = css`
  position: sticky;
  top: 0;
  z-index: 1!important;
  border-bottom: 1px solid ${blueGrey[50]};
`;

export const thStickyHeadShadow = css`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  z-index: 1!important;
  box-shadow: 0px 0px 3px 1px rgb(220, 220, 220);
  clip-path: inset(1px 0px -3px 0px);
`;

export const th = css`
  position: relative;
  white-space: nowrap;
  font-weight: 500;
  background-color: #ffffff;
  
  padding: 0px 16px;
`;

export const thPinned = css`
  position: sticky;
  z-index: 2!important;
`;

export const thLeftPinned = css`
`;

// Covers shadows overlap
export const thLeftPinnedShadow = css`
  position: absolute;
  right: -5px;
  top: 0;
  width: 5px;
  height: 100%;
  z-index: 1!important;
  background: linear-gradient(90deg, #ffffff, #ffffff 66%, #ffffff00);

  &:after{
    content: ' ';
    position: absolute;
    right: 5px;
    top: 0;
    width: 1px;
    height: 100%;
    z-index: 2!important;
    box-shadow: 0px 0px 4px 1px rgb(190, 190, 190);
    clip-path: inset(0px -4px -1px 1px);
  }
`;

export const tdLeftPinnedShadow = css`
  position: absolute;
  right: 0;
  top: 0;
  width: 1px;
  height: 100%;
  z-index: 1!important;
  box-shadow: 0px 0px 4px 1px rgb(190, 190, 190);
  clip-path: inset(0px -4px -1px 1px);
`;

export const thRightPinned = css`
`;

export const tdStickyBottom = css`
  position: sticky;
  bottom: 0;
  background-color: #ffffff;
`;

export const tdStickyBottomShadow = css`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 1px;
  z-index: 1!important;
  box-shadow: 0px 0px 3px 1px rgb(220, 220, 220);
  clip-path: inset(-1px 0px 0px 0px);
`;

// Covers shadows overlap
export const tdStickyBottomLeftPinnedShadow = css`
  position: absolute;
  right: -5px;
  top: 0;
  width: 5px;
  height: 100%;
  z-index: 1!important;
  background: linear-gradient(90deg, #ffffff, #ffffff 66%, #ffffff00);

  &:after{
    content: ' ';
    position: absolute;
    right: 5px;
    top: 0;
    width: 1px;
    height: 100%;
    z-index: 2!important;
    box-shadow: 0px 0px 4px 1px rgb(190, 190, 190);
    clip-path: inset(0px -4px -1px 1px);
  }
`;

// Mirror of tdStickyBottom for a row pinned to the TOP of the scroll body.
// Unlike the bottom row (which is the last row, so nothing paints over it),
// a top-pinned row is followed by data rows in the DOM — so it needs an
// explicit z-index to stay above them as they scroll underneath. The `top`
// offset (header height) is set inline since the header height is dynamic.
export const tdStickyTop = css`
  position: sticky;
  z-index: 2;
  background-color: #ffffff;
`;

// Left-pinned cell of a top-pinned row is sticky on BOTH axes (top + left),
// so it must sit above its own row's center cells during horizontal scroll.
export const tdStickyTopPinned = css`
  z-index: 3;
`;

export const tdStickyTopShadow = css`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 1px;
  z-index: 1!important;
  box-shadow: 0px 0px 3px 1px rgb(220, 220, 220);
  clip-path: inset(0px 0px -1px 0px);
`;

// Covers shadows overlap
export const tdStickyTopLeftPinnedShadow = css`
  position: absolute;
  right: -5px;
  top: 0;
  width: 5px;
  height: 100%;
  z-index: 1!important;
  background: linear-gradient(90deg, #ffffff, #ffffff 66%, #ffffff00);

  &:after{
    content: ' ';
    position: absolute;
    right: 5px;
    top: 0;
    width: 1px;
    height: 100%;
    z-index: 2!important;
    box-shadow: 0px 0px 4px 1px rgb(190, 190, 190);
    clip-path: inset(0px -4px -1px 1px);
  }
`;

export const head = css`
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover .sortButton {
    visibility: visible;
    width: auto;
  }

  &:hover .sortIndicator {
    display: none;
  }
`;

export const headTitleContainer = css`
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  flex: 1;
  column-gap: 8px;
`;

export const headTitleContainerRight = css`
  justify-content: flex-end;
`;

export const headTitle = css`
  color: ${grey[500]};
  font-size: ${campaignManagerTheme.fontSize.small};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const sortIndicator = css`
  display: inline-flex;
  align-items: center;
  padding: 5px;
`;

export const isNotSorted = css`
  visibility: hidden;
`;

export const isSorted = css`
  visibility: display;
`;

export const sortButtonIcon = css`
  color: ${blueGrey[400]};
`;

export const tr = css``;

export const td = css`
  padding: 0px 16px;

  background-color: #ffffff;
    
  border-bottom: 1px solid ${blueGrey[50]};
  tr:last-of-type & {
    border-bottom: none;
  }
`;

export const tdVerticalAlignTop = css`
padding-top: 8px;
padding-bottom: 8px;
vertical-align: top;
`;

export const tdEdgeState = css`
padding: 0px 16px;
overflow: hidden;
`;

export const tdPinned = css`
  position: sticky;
  z-index: 1;
`;

export const tdLeftPinned = css`
  box-shadow: -4px 0px 10px 2px #00000042;
  clip-path: inset(0px -12px 0px 0px);
`;

export const tdRightPinned = css`
  box-shadow: 4px 0px 10px 2px #00000042;
  clip-path: inset(0px 0px 0px -12px);
`;

export const cell = css`
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const cellVerticalAlignTop = css`
  min-height: 40px;
  align-items: start;
`;

export const cellRight = css`
  justify-content: flex-end;
`;

export const resizeHandleContainer = css`
  position: relative;
  margin-left: 0.75rem;
  height: 20px;
`;

export const resizeHandle = css`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 6px;
  transform: translateX(50%);
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  background-color: transparent;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.15s ease;

  &::before {
    content: '';
    position: absolute;
    top: 25%;
    bottom: 25%;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background-color: ${blueGrey[300]};
  }

  th:hover > & {
    opacity: 1;
  }

  &:hover,
  &.resizing {
    opacity: 1;

    &::before {
      background-color: ${campaignManagerTheme.color.primary};
      width: 2px;
    }
  }
`;

export const resizeGuideline = css`
  position: fixed;
  width: 0;
  border-left: 2px dashed ${campaignManagerTheme.color.primary};
  pointer-events: none;
  z-index: 9999;
  transform: translateX(-1px);
`;

export const resizeHint = css`
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  user-select: none;
  background: ${blueGrey[900]};
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
`;

export const footer = css`
  padding-left: 16px;
  margin-top: 16px;
`;

export const leftBoundary = css`
  box-shadow: -1px 5px 6px 0px #000000f2;
  clip-path: inset(0px -12px 0px 0px);
  transition: opacity 0.1s ease 0s;
  opacity: 0;
  background: #ffffff;
  left: 0px;
  width: 1px;
  position: sticky;
  pointer-events: none;
  z-index: 90;
`;

export const rightBoundary = css`
  box-shadow: 1px 5px 6px 0px #000000f2;
  clip-path: inset(0px 0px 0px -12px);
  transition: opacity 0.1s ease 0s;
  opacity: 0;
  background: #ffffff;
  right: 0px;
  width: 1px;
  position: sticky;
  pointer-events: none;
  z-index: 90;
`;

export const tfoot = css`
  background: #ffffff;
`;
